import React, { useEffect, useState } from 'react';
import { useRecordContext, useDataProvider, useNotify } from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Switch,
    FormControlLabel,
    Button,
    Typography,
    CircularProgress,
    Tabs,
    Tab,
    Card,
    CardContent,
    Chip,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Checkbox,
    IconButton,
    Tooltip,
    Collapse
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Info as InfoIcon
} from '@mui/icons-material';

/**
 * Enhanced Product Feature Toggles Component
 * 
 * Supports BOTH organization-level and code-level feature management.
 * Critical for organizations with many enrollment codes.
 * 
 * Features:
 * - Organization-level defaults
 * - Per-code feature overrides
 * - Bulk code operations
 * - Inheritance visualization
 * - WIC Standard Package templates
 */
const ProductFeatureTogglesEnhanced = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [searchParams, setSearchParams] = useSearchParams();

    // State - initialize from URL or default to 'organization-defaults'
    const [currentTab, setCurrentTab] = useState(
        searchParams.get('featureTab') || 'organization-defaults'
    );
    const [allFeatures, setAllFeatures] = useState([]);
    const [orgFeatures, setOrgFeatures] = useState({});
    const [orgCodes, setOrgCodes] = useState([]);
    const [selectedCodes, setSelectedCodes] = useState([]);
    const [expandedCodes, setExpandedCodes] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [codeFilter, setCodeFilter] = useState('all'); // 'all', 'withFeatures', 'noFeatures'

    // Load data
    useEffect(() => {
        if (record) {
            loadData();
        }
    }, [record, dataProvider, notify]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [allFeaturesRes, orgFeaturesRes, codesRes] = await Promise.all([
                dataProvider.getList('product-features', {
                    pagination: { page: 1, perPage: 100 },
                    sort: { field: 'display_name', order: 'ASC' }
                }),
                dataProvider.getOne('organizations', { id: `${record.id}/product_features` }),
                // Fetch codes for this organization using data provider
                dataProvider.getList('codes', {
                    filter: { organization_id: record.id },
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'code_name', order: 'ASC' }
                })
            ]);

            console.log('[Product Features] Data loaded:', {
                allFeatures: allFeaturesRes.data,
                orgFeatures: orgFeaturesRes.data,
                codes: codesRes.data
            });
            console.log('[Product Features] All features with scope:', 
                allFeaturesRes.data.map(f => ({ name: f.name, scope: f.scope, id: f.id }))
            );
            console.log('[Product Features] Filtered features (ORGANIZATION or BOTH):', 
                allFeaturesRes.data.filter(f => f.scope === 'ORGANIZATION' || f.scope === 'BOTH').map(f => f.name)
            );
            // Ensure each code has a features object (initialize if missing)
            const codesWithFeatures = (codesRes.data || []).map(code => ({
                ...code,
                features: code.features || {}
            }));

            setAllFeatures(allFeaturesRes.data);
            setOrgFeatures(orgFeaturesRes.data);
            setOrgCodes(codesWithFeatures);
            setLoading(false);
        } catch (error) {
            console.error("Error loading data:", error);
            notify('Error loading product features', { type: 'error' });
            setLoading(false);
        }
    };

    // Organization-level handlers
    const handleOrgFeatureChange = (featureName) => (event) => {
        setOrgFeatures({
            ...orgFeatures,
            [featureName]: event.target.checked,
        });
    };

    const handleSaveOrgFeatures = async () => {
        setSaving(true);
        try {
            await dataProvider.update('organizations', {
                id: `${record.id}/product_features`,
                data: orgFeatures,
                previousData: {}
            });
            notify('Organization features updated successfully', { type: 'success' });
            await loadData(); // Reload to reflect changes in code-level view
        } catch (error) {
            console.error("Error saving features:", error);
            notify('Error saving organization features', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // Code-level handlers
    const toggleCodeSelection = (codeId) => {
        setSelectedCodes(prev => 
            prev.includes(codeId) 
                ? prev.filter(id => id !== codeId)
                : [...prev, codeId]
        );
    };

    const toggleAllCodes = () => {
        const filteredCodes = getFilteredCodes();
        if (selectedCodes.length === filteredCodes.length) {
            setSelectedCodes([]);
        } else {
            setSelectedCodes(filteredCodes.map(c => c.code_id));
        }
    };

    const handleBulkUpdateCodes = async (features) => {
        if (selectedCodes.length === 0) {
            notify('Please select at least one code', { type: 'warning' });
            return;
        }

        setSaving(true);
        try {
            const response = await fetch(`/api/organizations/${record.id}/codes/bulk_update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({
                    code_ids: selectedCodes,
                    features: features
                })
            });

            const result = await response.json();
            
            if (response.ok) {
                notify(`Bulk update completed: ${result.results.success_count} succeeded`, { type: 'success' });
                setSelectedCodes([]);
                await loadData();
            } else {
                notify(`Bulk update failed: ${result.message}`, { type: 'error' });
            }
        } catch (error) {
            console.error("Error bulk updating codes:", error);
            notify('Error performing bulk update', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const applyWICStandardPackage = () => {
        // WIC Standard Package identified in production (358 codes)
        const wicPackage = {
            lactationOnDemandVideo: true,
            lactationOnDemandAudio: true,
            lactationScheduling: true,
            resourceLibrary: true,
            birthWishList: true
        };
        handleBulkUpdateCodes(wicPackage);
    };

    const applyHealthPlanPremium = () => {
        // Health Plan Premium Package (e.g., Molina Nebraska)
        const premiumPackage = {
            lactationOnDemandVideo: true,
            lactationOnDemandAudio: true,
            lactationScheduling: true,
            resourceLibrary: true,
            birthWishList: true,
            doulaOnDemandVideo: true,
            doulaOnDemandChat: true,
            doulaSchedulingVirtual: true
        };
        handleBulkUpdateCodes(premiumPackage);
    };

    const toggleCodeExpansion = (codeId) => {
        const newExpanded = new Set(expandedCodes);
        if (newExpanded.has(codeId)) {
            newExpanded.delete(codeId);
        } else {
            newExpanded.add(codeId);
        }
        setExpandedCodes(newExpanded);
    };

    const getFilteredCodes = () => {
        if (codeFilter === 'all') return orgCodes;
        
        return orgCodes.filter(code => {
            const hasCodeFeatures = allFeatures.some(feature => 
                code.features[feature.name] === true
            );
            return codeFilter === 'withFeatures' ? hasCodeFeatures : !hasCodeFeatures;
        });
    };

    const countEnabledFeatures = (features) => {
        return allFeatures.filter(f => features[f.name] === true).length;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={2}>
            <Typography variant="h6" gutterBottom>
                Product Features
            </Typography>

            <Tabs 
                value={currentTab} 
                onChange={(e, v) => {
                    setCurrentTab(v);
                    setSearchParams({ featureTab: v });
                }} 
                sx={{ mb: 2 }}
            >
                <Tab label="Organization Defaults" value="organization-defaults" />
                <Tab 
                    value="code-level-features"
                    label={
                        <Box display="flex" alignItems="center" gap={1}>
                            <span>Code-Level Features</span>
                            <Chip 
                                label={orgCodes.length} 
                                size="small" 
                                color="primary"
                            />
                        </Box>
                    } 
                />
            </Tabs>

            {/* TAB 1: Organization-Level Defaults */}
            {currentTab === 'organization-defaults' && (
                <Card>
                    <CardContent>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                These are default features for the organization. 
                                Users with enrollment codes will inherit these unless the code has specific overrides.
                            </Typography>
                        </Alert>

                        {allFeatures
                            .filter(f => f.scope === 'ORGANIZATION' || f.scope === 'BOTH')
                            .map(feature => (
                            <Box key={feature.id} mb={2}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={orgFeatures[feature.name] || false}
                                            onChange={handleOrgFeatureChange(feature.name)}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography variant="body1">
                                                    {feature.display_name}
                                                </Typography>
                                                {feature.scope === 'BOTH' && (
                                                    <Chip label="Can override at code level" size="small" />
                                                )}
                                            </Box>
                                            {feature.description && (
                                                <Typography variant="caption" color="textSecondary">
                                                    {feature.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                />
                            </Box>
                        ))}

                        <Box mt={3}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSaveOrgFeatures}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save Organization Defaults'}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* TAB 2: Code-Level Features */}
            {currentTab === 'code-level-features' && (
                <Card>
                    <CardContent>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Code-level features override organization defaults.</strong>
                                {' '}This is critical for organizations with many enrollment codes.
                                Use bulk operations to manage multiple codes efficiently.
                            </Typography>
                        </Alert>

                        {/* Bulk Operations Toolbar */}
                        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Bulk Operations ({selectedCodes.length} codes selected)
                            </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={applyWICStandardPackage}
                                    disabled={saving || selectedCodes.length === 0}
                                >
                                    Apply WIC Standard Package
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={applyHealthPlanPremium}
                                    disabled={saving || selectedCodes.length === 0}
                                >
                                    Apply Health Plan Premium
                                </Button>
                                <Tooltip title="Select/Deselect all filtered codes">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={toggleAllCodes}
                                    >
                                        {selectedCodes.length === getFilteredCodes().length ? 'Deselect All' : 'Select All'}
                                    </Button>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Codes Table */}
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedCodes.length === getFilteredCodes().length && getFilteredCodes().length > 0}
                                            indeterminate={selectedCodes.length > 0 && selectedCodes.length < getFilteredCodes().length}
                                            onChange={toggleAllCodes}
                                        />
                                    </TableCell>
                                    <TableCell>Code Name</TableCell>
                                    <TableCell>Enabled Features</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getFilteredCodes().map(code => {
                                    const featureCount = countEnabledFeatures(code.features);
                                    const isExpanded = expandedCodes.has(code.code_id);

                                    return (
                                        <React.Fragment key={code.code_id}>
                                            <TableRow>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedCodes.includes(code.code_id)}
                                                        onChange={() => toggleCodeSelection(code.code_id)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {code.code_name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={`${featureCount} / ${allFeatures.length}`}
                                                        size="small"
                                                        color={featureCount > 0 ? 'success' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {featureCount === 0 ? (
                                                        <Chip 
                                                            icon={<InfoIcon />}
                                                            label="Inherits from org"
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    ) : (
                                                        <Chip 
                                                            icon={<CheckCircleIcon />}
                                                            label="Has overrides"
                                                            size="small"
                                                            color="primary"
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleCodeExpansion(code.code_id)}
                                                    >
                                                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={5} sx={{ py: 0 }}>
                                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                            <Typography variant="subtitle2" gutterBottom>
                                                                Features for {code.code_name}
                                                            </Typography>
                                                            <Box display="flex" flexWrap="wrap" gap={1}>
                                                                {allFeatures.map(feature => {
                                                                    const isEnabled = code.features[feature.name];
                                                                    return (
                                                                        <Chip
                                                                            key={feature.id}
                                                                            label={feature.display_name}
                                                                            size="small"
                                                                            color={isEnabled ? 'success' : 'default'}
                                                                            icon={isEnabled ? <CheckCircleIcon /> : <CancelIcon />}
                                                                        />
                                                                    );
                                                                })}
                                                            </Box>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>

                        {orgCodes.length === 0 && (
                            <Box textAlign="center" py={4}>
                                <Typography color="textSecondary">
                                    No enrollment codes found for this organization.
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default ProductFeatureTogglesEnhanced;

